import { Upload } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { generateCompanyLogoUrlDirect } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import getUploadURLCompanyLogo from '@src/APIs/graphQl/Company/getUploadURLCompanyLogo';
import { uploadFile } from '@src/App/Components/Dialogs/AnalyseSkillsDialog/AnalyseCvDialog';
import type { AppState } from '@store/store';
import React, { type ChangeEvent } from 'react';
import { useSelector } from 'react-redux';

interface LogoUploadCardProps {
    mode: 'light' | 'dark';
    logoId: string | null;
    onUploadStart: (text: string) => void;
    onUploadError: (error: string) => void;
    onUploadComplete: () => void;
    disabled?: boolean;
}

function LogoUploadCard({
    mode,
    logoId,
    onUploadStart,
    onUploadError,
    onUploadComplete,
    disabled = false,
}: LogoUploadCardProps): JSX.Element {
    const companyName = useSelector((state: AppState) => state.company.name);
    const companyId = useSelector((state: AppState) => state.company.companyId);
    const logoIdLight = useSelector((state: AppState) => state.company.style.logoIdLight);
    const logoIdDark = useSelector((state: AppState) => state.company.style.logoIdDark);
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;

    const logoUrl = generateCompanyLogoUrlDirect(companyId, logoIdLight, logoIdDark, 100, mode === 'dark');

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const file = e.target.files[0];

        onUploadStart('Uploading...');

        try {
            const post = await getUploadURLCompanyLogo(mode);
            if (!post) return;

            await uploadFile(post, file);
            onUploadStart('Transforming...');
            onUploadComplete();
        } catch (ex) {
            onUploadError(ex?.toString() || 'Upload failed');
        }
    };

    const isLight = mode === 'light';
    const backgroundColor = isLight ? '#ffffff' : '#121212';
    const borderColor = isLight ? '#e0e0e0' : '#333333';
    const textColor = isLight ? 'textSecondary' : '#ffffff';

    return (
        <Card sx={{ flex: 1, border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                    {isLight ? 'Light Theme Logo' : 'Dark Theme Logo'}
                </Typography>
                <Box
                    sx={{
                        height: 120,
                        bgcolor: backgroundColor,
                        border: `1px solid ${borderColor}`,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                    }}
                >
                    {logoId ? (
                        <Box
                            component="img"
                            src={logoUrl}
                            alt={`Logo ${companyName}`}
                            sx={{
                                maxWidth: 160,
                                height: 'auto',
                                maxHeight: 48,
                                p: 1,
                                objectFit: 'contain',
                            }}
                        />
                    ) : (
                        <Typography variant="body2" sx={{ color: textColor }}>
                            {langStrings.noPreviewAvailable}
                        </Typography>
                    )}
                </Box>
                <Button component="label" startIcon={<Upload />} disabled={disabled} size="small" fullWidth>
                    {isLight ? 'Light Logo hochladen' : 'Dark Logo hochladen'}
                    <input type="file" accept="image/*" hidden onChange={handleFileChange} />
                </Button>
            </CardContent>
        </Card>
    );
}

export default LogoUploadCard;
